/**
 * Ticket SK5: Öffentliche Kursseite.
 *
 * Die Adresse ist die pflegbare öffentliche Adresse des Katalogeintrags
 * (`public_slug`), nicht die interne Kennung. Nur aktive, kaufbare Kurse mit
 * Kursverknüpfung sind erreichbar; alles andere liefert die 404-Seite.
 *
 * Ticket SK15: Der Kauf startet unmittelbar hier. Die frühere Zwischenseite
 * `/kaufen/<interne Kennung>` leitet dauerhaft auf diese Seite um.
 *
 * Ticket WS3: Diese Seite ist für Suchmaschinen freigegeben und steht in der
 * Sitemap, solange der Kurs aktiv und kaufbar ist. Die Kaufstrecke selbst
 * bleibt gesperrt – eine Bezahlseite gehört nicht in den Index.
 *
 * Preise erscheinen ausschließlich über `formatServicePrice` – niemals als
 * Zahl im Text.
 */

import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Layout } from "@/components/Layout";
import { CourseCurriculum } from "@/components/CourseCurriculum";
import { OfferPeople } from "@/components/OfferPeople";
import { PurchaseBox } from "@/components/checkout/PurchaseBox";
import { hideOnError } from "@/lib/img-fallback";
import { normalizeMarkdown } from "@/lib/markdown-normalize";
import { getPublicCourse } from "@/lib/courses-public.functions";

import { formatServicePrice } from "@/lib/service-format";

export const Route = createFileRoute("/wissen/kurse/$slug")({
  loader: async ({ params }) => {
    const course = await getPublicCourse({ data: { slug: params.slug } });
    if (!course) throw notFound();
    return { course };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Nicht verfügbar | creategy" },
          { name: "robots", content: "noindex, nofollow" },
        ],
      };
    }
    const { course } = loaderData;
    const title = course.seoTitle || `${course.title} | Kurs von creategy`;
    const description =
      course.seoDescription ||
      course.shortDescription ||
      `${course.title}: Kurs von creategy für Wahlkampf und politische Kommunikation.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        // Ticket SK8: bewusst kein og:image. Das Produktbild wird über eine
        // ablaufende, serverseitig erzeugte Adresse ausgeliefert; als Vorschau
        // beim Teilen taugt sie nicht, weil geteilte Verweise sonst später kein
        // Bild mehr zeigen. Ein dauerhaftes Vorschaubild folgt getrennt.
        { name: "twitter:card", content: "summary" },
      ],
    };
  },
  component: CourseDetailPage,
});

function CourseDetailPage() {
  const { course } = Route.useLoaderData();

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Link
            to="/wissen/kurse"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Zurück zur Übersicht
          </Link>

          <h1 className="heading-h1 mt-6 text-primary">{course.title}</h1>

          <div className="mt-10 grid gap-10 lg:mt-14 lg:grid-cols-[3fr_2fr] lg:gap-16">
            <div className="order-2 space-y-10 lg:order-1">
              {/* Kurzbeschreibung bewusst ausgeblendet – sie erscheint auf der
                  Übersichtsseite; auf der Detailseite führt die Langbeschreibung. */}

              {course.longDescription && (
                <div className="markdown-article markdown-article--wide font-body text-foreground">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {normalizeMarkdown(course.longDescription)}
                  </ReactMarkdown>

                </div>
              )}

              {/* Ticket W11: Kapitelübersicht und Personen. Beide Bausteine
                  zeichnen nichts, wenn keine Inhalte hinterlegt sind. */}
              <CourseCurriculum curriculum={course.curriculum} />
              <OfferPeople people={course.people} />
            </div>

            <aside className="order-1 space-y-8 lg:order-2">
              {/* Ticket SK8: Produktbild. Ohne Bild erscheint hier nichts; eine
                  abgelaufene Adresse fällt still weg statt kaputt zu erscheinen. */}
              {course.imageUrl && (
                <div className="aspect-[3/4] w-[56%] overflow-hidden rounded-sm mx-auto lg:w-[70%] lg:mx-0">
                  <img
                    src={course.imageUrl}
                    alt={course.title}
                    width={800}
                    height={500}
                    decoding="async"
                    onError={hideOnError}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}

              {/* Ticket SK15: Kauf ohne Zwischenstation – Kurzangaben, Preis,
                  Bestätigung, Knopf und Hinweise in einer geschlossenen Einheit. */}
              <PurchaseBox
                serviceKey={course.key}
                priceLabel={formatServicePrice(course.priceCents, course.priceKind)}
              />

              <div className="rounded-sm border border-foreground/15 p-6 md:p-8">
                <h2 className="heading-prose-h3 text-primary">
                  Was nach dem Kauf geschieht
                </h2>
                <ul className="prose-small mt-4 space-y-3 text-muted-foreground">
                  <li>
                    Sie erhalten Ihre Zugangsdaten per E-Mail und arbeiten den Kurs im
                    Kampagnencockpit durch – in Ihrem Tempo, beliebig oft.
                  </li>
                  <li>
                    Bestehende Zugänge bleiben erhalten: Wer schon ein Konto bei uns hat, findet den
                    Kurs dort zusätzlich.
                  </li>
                  <li>Die Rechnung erhalten Sie gesondert per E-Mail.</li>
                </ul>
              </div>

            </aside>
          </div>
        </div>
      </section>
    </Layout>
  );
}
